import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import CodeBlock from '../components/CodeBlock';
import { guides } from '../data/guides';

const PREFACE_CONTENT = `# Welcome to the System Design Masterclass

## By SweetCodey — *Interview Simplified*

---

This interactive book covers **13 in-depth system design topics**, from foundational principles to complex real-world architectures.

Each chapter follows a consistent structure:
- **Requirements** — What are we building?
- **API Design** — How do clients interact?
- **High-Level Design** — The big picture
- **Deep Dive** — The details that matter

Start with Chapter 1 (System Design Basics) if you're new, or jump to any chapter that interests you.

> *"The goal is not to memorize architectures — it's to internalize a way of thinking about systems."*
> — The SweetCodey Team

---

**Happy reading! Use the sidebar to navigate chapters.**`;

const BookReaderPage = () => {
  const [currentChapter, setCurrentChapter] = useState(0);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [readChapters, setReadChapters] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('sc_read_chapters') || '[]');
    } catch {
      return [];
    }
  });
  const contentRef = useRef(null);

  const chapters = [
    { title: 'Preface', file: null, emoji: '📖' },
    ...guides.map(g => ({ title: g.title, file: g.contentFile, emoji: g.emoji })),
  ];

  useEffect(() => {
    const ch = chapters[currentChapter];
    if (!ch.file) {
      setContent(PREFACE_CONTENT);
      setLoading(false);
      return;
    }
    setLoading(true);
    setContent('');
    fetch(ch.file)
      .then(r => r.text())
      .then(text => { setContent(text); setLoading(false); })
      .catch(() => { setContent('Content unavailable.'); setLoading(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChapter]);

  const goToChapter = (idx) => {
    setCurrentChapter(idx);
    if (!readChapters.includes(idx)) {
      const updated = [...readChapters, idx];
      setReadChapters(updated);
      localStorage.setItem('sc_read_chapters', JSON.stringify(updated));
    }
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalProgress = Math.round((readChapters.length / chapters.length) * 100);

  return (
    <div className="book-reader">
      {/* Top bar */}
      <div className="book-topbar">
        <div className="book-topbar-left">
          <button
            className="book-sidebar-toggle"
            onClick={() => setSidebarOpen(o => !o)}
            aria-label="Toggle sidebar"
          >
            {sidebarOpen ? '◀' : '☰'}
          </button>
          <Link to="/" className="book-home-link">
            <span className="logo-mark-icon">📚</span>
            <span>SweetCodey</span>
          </Link>
          <span className="book-title-crumb">System Design Masterclass</span>
        </div>
        <div className="book-topbar-right">
          <div className="book-progress-wrap">
            <span className="book-progress-label">{totalProgress}% complete</span>
            <div className="book-progress-bar">
              <div className="book-progress-fill" style={{ width: `${totalProgress}%` }} />
            </div>
          </div>
          <Link to="/course" className="book-enroll-btn">Get Full Access</Link>
        </div>
      </div>

      <div className="book-layout">
        {/* Sidebar */}
        {sidebarOpen && (
          <aside className="book-sidebar">
            <div className="book-sidebar-header">
              <span className="book-sidebar-title">Chapters</span>
              <span className="book-sidebar-count">{chapters.length} chapters</span>
            </div>
            <ul className="book-chapter-list">
              {chapters.map((ch, i) => (
                <li key={i}>
                  <button
                    className={[
                      'book-chapter-btn',
                      currentChapter === i ? 'active' : '',
                      readChapters.includes(i) ? 'read' : '',
                    ].join(' ')}
                    onClick={() => goToChapter(i)}
                  >
                    <span className="book-chapter-emoji">{ch.emoji}</span>
                    <span className="book-chapter-name">
                      {i === 0 ? ch.title : `${i}. ${ch.title}`}
                    </span>
                    {readChapters.includes(i) && currentChapter !== i && (
                      <span className="book-chapter-check">✓</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </aside>
        )}

        {/* Content area */}
        <main className="book-content" ref={contentRef}>
          <div className="book-chapter-header">
            <span className="book-chapter-num">
              {currentChapter === 0 ? 'Preface' : `Chapter ${currentChapter}`}
            </span>
            <h1 className="book-chapter-title">
              {chapters[currentChapter].emoji} {chapters[currentChapter].title}
            </h1>
          </div>

          {loading ? (
            <div className="book-loading">
              <div className="book-loading-spinner" />
              <p>Loading chapter…</p>
            </div>
          ) : (
            <div className="book-markdown">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={{
                  // Block code via pre — fixes react-markdown v10 inline detection
                  pre: ({ children }) => {
                    const child = React.Children.toArray(children).find(
                      c => React.isValidElement(c) && c.type === 'code'
                    );
                    if (child) {
                      const { className = '', children: codeContent } = child.props;
                      const lang = className.replace('language-', '') || 'text';
                      return <CodeBlock code={String(codeContent).replace(/\n$/, '')} lang={lang} />;
                    }
                    return <pre>{children}</pre>;
                  },
                  // Inline code only
                  code: ({ children }) => (
                    <code className="inline-code">{children}</code>
                  ),
                  img: ({ src, alt }) => (
                    <figure className="book-figure">
                      <img src={src} alt={alt || ''} loading="lazy" />
                      {alt && <figcaption>{alt}</figcaption>}
                    </figure>
                  ),
                  table: ({ children }) => (
                    <div className="guide-table-wrap"><table>{children}</table></div>
                  ),
                  blockquote: ({ children }) => (
                    <blockquote className="guide-blockquote">{children}</blockquote>
                  ),
                  a: ({ href, children }) => (
                    <a href={href} className="guide-link"
                      target={href?.startsWith('http') ? '_blank' : undefined}
                      rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}>
                      {children}
                    </a>
                  ),
                }}
              >
                {content}
              </ReactMarkdown>
            </div>
          )}

          {/* Chapter navigation */}
          <div className="book-chapter-nav">
            {currentChapter > 0 && (
              <button
                className="book-nav-btn prev"
                onClick={() => goToChapter(currentChapter - 1)}
              >
                ← {chapters[currentChapter - 1].title}
              </button>
            )}
            {currentChapter < chapters.length - 1 && (
              <button
                className="book-nav-btn next"
                onClick={() => goToChapter(currentChapter + 1)}
              >
                {chapters[currentChapter + 1].title} →
              </button>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default BookReaderPage;
